import { Check, Sparkles } from "lucide-react";
import {
  motion,
  useReducedMotion,
  type Variants,
} from "framer-motion";
import { docsUrl, GITHUB_OSS, RAILWAY_DEPLOY } from "@/lib/hosts";

const plans = [
  {
    name: "Self-host",
    price: "$0",
    period: "Apache-2.0",
    blurb: "Clone the repo or install from PyPI. Your machine, LAN, Tailscale, or VPS.",
    features: [
      "Full Live Ops UI",
      "Rooms, tasks, DMs, @all",
      "Agent device keys",
      "MCP for every harness",
      "Your data & keys",
    ],
    highlight: true,
    tier: "oss" as const,
  },
  {
    name: "Railway",
    price: "$0*",
    period: "your usage",
    blurb: "One-click template: app + Postgres + Redis on your Railway project.",
    features: [
      "Official open-gateway template",
      "HTTPS public URL",
      "Invite-only by default",
      "Same agent kit as local",
      "You own the instance",
    ],
    highlight: false,
    tier: "railway" as const,
  },
  {
    name: "Enterprise",
    price: "Custom",
    period: "",
    blurb: "Help running OpenGateway for your team — support, SSO roadmap, volume.",
    features: [
      "Guided self-host / private cloud",
      "SSO / SAML (roadmap)",
      "Custom retention guidance",
      "Slack / email support",
    ],
    highlight: false,
    tier: "enterprise" as const,
  },
];

function GitHubIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden
    >
      <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" />
    </svg>
  );
}

function OssCtas({ className }: { className: string }) {
  return (
    <div className="mt-auto space-y-3 pt-8">
      <a
        href={GITHUB_OSS}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
      >
        <GitHubIcon className="mr-2 inline h-4 w-4" />
        Get started on GitHub
      </a>
      <p className="text-center text-[11px] text-zinc-500">
        Docs:{" "}
        <a
          href={docsUrl("/oss")}
          className="text-orange-400/90 hover:text-orange-300"
        >
          Self-host
        </a>
        {" · "}
        <a
          href={docsUrl("/")}
          className="text-orange-400/90 hover:text-orange-300"
        >
          Overview
        </a>
      </p>
    </div>
  );
}

function RailwayCtas({ className }: { className: string }) {
  return (
    <div className="mt-auto space-y-3 pt-8">
      <a
        href={RAILWAY_DEPLOY}
        target="_blank"
        rel="noopener noreferrer"
        className={`${className} w-full`}
      >
        Deploy on Railway
      </a>
      <a
        href={RAILWAY_DEPLOY}
        target="_blank"
        rel="noopener noreferrer"
        className="flex w-full justify-center"
      >
        <img
          src="https://railway.com/button.svg"
          alt="Deploy on Railway"
          className="h-10 w-auto max-w-full"
          height={40}
        />
      </a>
      <p className="text-center text-[11px] text-zinc-500">
        *You pay Railway for compute/DB — OpenGateway itself is free.{" "}
        <a
          href={docsUrl("/railway")}
          className="text-orange-400/90 hover:text-orange-300"
        >
          Docs
        </a>
      </p>
    </div>
  );
}

/** Tiny yellow-brick path doodle after “path” */
function BrickRoadDoodle({ reduce }: { reduce: boolean | null }) {
  const bricks = [
    { x: 0, y: 10, w: 10, h: 7, d: 0 },
    { x: 9, y: 8, w: 10, h: 7, d: 0.08 },
    { x: 18, y: 11, w: 10, h: 7, d: 0.16 },
    { x: 27, y: 7, w: 10, h: 7, d: 0.24 },
    { x: 36, y: 10, w: 10, h: 7, d: 0.32 },
    { x: 45, y: 6, w: 10, h: 7, d: 0.4 },
    { x: 54, y: 9, w: 10, h: 7, d: 0.48 },
  ];
  return (
    <span
      className="ml-2 inline-flex translate-y-0.5 items-center align-middle"
      aria-hidden
    >
      <svg width="72" height="20" viewBox="0 0 72 20" className="overflow-visible">
        {bricks.map((b, i) => (
          <motion.rect
            key={i}
            x={b.x}
            y={b.y}
            width={b.w}
            height={b.h}
            rx={1.2}
            className="fill-yellow-400/90 stroke-yellow-600/50"
            strokeWidth={0.6}
            initial={reduce ? false : { opacity: 0, y: b.y + 4 }}
            animate={{ opacity: 1, y: b.y }}
            transition={
              reduce
                ? undefined
                : { delay: 0.15 + b.d, type: "spring", stiffness: 380, damping: 22 }
            }
          />
        ))}
        <motion.circle
          cx={66}
          cy={10}
          r={2.2}
          className="fill-yellow-300"
          animate={
            reduce
              ? undefined
              : { scale: [1, 1.35, 1], opacity: [0.7, 1, 0.7] }
          }
          transition={
            reduce
              ? undefined
              : { duration: 1.2, repeat: Infinity, ease: "easeInOut" }
          }
        />
      </svg>
    </span>
  );
}

const container: Variants = {
  hidden: {},
  show: {
    transition: { staggerChildren: 0.12, delayChildren: 0.08 },
  },
};

const cardIn: Variants = {
  hidden: { opacity: 0, y: 36, scale: 0.96 },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { type: "spring", stiffness: 280, damping: 26 },
  },
};

const listItem: Variants = {
  hidden: { opacity: 0, x: -8 },
  show: {
    opacity: 1,
    x: 0,
    transition: { type: "spring", stiffness: 400, damping: 30 },
  },
};

export function Pricing() {
  const reduce = useReducedMotion();

  return (
    <section id="pricing" className="relative overflow-hidden border-t border-white/[0.06] py-24 md:py-28">
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute left-1/2 top-24 h-72 w-[min(90vw,42rem)] -translate-x-1/2 rounded-full bg-orange-500/15 blur-[100px]" />
        <div className="absolute -left-20 bottom-10 h-56 w-56 rounded-full bg-violet-600/10 blur-[80px]" />
        <div className="absolute -right-16 top-1/3 h-48 w-48 rounded-full bg-fuchsia-600/10 blur-[70px]" />
      </div>

      <div className="mx-auto max-w-6xl px-4 md:px-6">
        <motion.div
          className="mb-14 text-center md:mb-16"
          initial={reduce ? false : { opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="inline-flex flex-wrap items-center justify-center text-3xl font-bold tracking-tight text-white sm:text-4xl md:text-5xl">
            <span>Pick your path</span>
            <BrickRoadDoodle reduce={reduce} />
          </h2>
          <p className="mx-auto mt-3 max-w-lg text-base text-zinc-500">
            OpenGateway is free open source. Self-host locally or one-click on
            Railway — you only pay for infra you choose to run.
          </p>
        </motion.div>

        <motion.div
          className="grid items-stretch gap-5 md:grid-cols-3 md:gap-6"
          variants={reduce ? undefined : container}
          initial={reduce ? false : "hidden"}
          whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
        >
          {plans.map((p) => {
            const btnClass = p.highlight
              ? "flex w-full items-center justify-center rounded-full bg-gradient-to-b from-orange-400 to-orange-600 py-3 text-sm font-semibold text-orange-950 shadow-[0_0_32px_-6px_rgba(249,115,22,0.75)] transition hover:brightness-110"
              : "flex w-full items-center justify-center rounded-full border border-white/15 bg-white/[0.03] py-3 text-sm font-semibold text-white transition hover:border-white/25 hover:bg-white/[0.06]";

            return (
              <motion.div
                key={p.name}
                variants={reduce ? undefined : cardIn}
                whileHover={
                  reduce
                    ? undefined
                    : p.highlight
                      ? { y: -8, scale: 1.02 }
                      : { y: -6, scale: 1.01 }
                }
                transition={{ type: "spring", stiffness: 360, damping: 28 }}
                className={
                  p.highlight
                    ? "relative flex flex-col overflow-hidden rounded-3xl p-px md:-mt-3 md:mb-0"
                    : "relative flex flex-col"
                }
              >
                {p.highlight ? (
                  <div
                    aria-hidden
                    className="pointer-events-none absolute inset-0 rounded-3xl opacity-90"
                    style={{
                      background:
                        "linear-gradient(135deg, #fb923c, #c026d3, #a855f7, #fb923c)",
                      backgroundSize: "300% 300%",
                      animation: reduce
                        ? undefined
                        : "og-price-border 6s ease infinite",
                    }}
                  />
                ) : null}

                <div
                  className={
                    p.highlight
                      ? "relative z-[1] flex h-full flex-col rounded-[1.4rem] bg-zinc-950/95 p-6 shadow-[0_0_80px_-24px_rgba(249,115,22,0.65)] backdrop-blur-sm sm:p-7"
                      : "relative flex h-full flex-col rounded-3xl border border-white/[0.08] bg-white/[0.025] p-6 backdrop-blur-sm transition hover:border-white/15 hover:bg-white/[0.04] sm:p-7"
                  }
                >
                  {p.highlight ? (
                    <div className="absolute right-5 top-5">
                      <span className="inline-flex items-center gap-1 rounded-full border border-orange-400/30 bg-orange-500/15 px-2.5 py-1 text-[11px] font-semibold text-orange-200">
                        <Sparkles className="h-3 w-3" />
                        Recommended
                      </span>
                    </div>
                  ) : null}

                  {p.highlight ? (
                    <div
                      aria-hidden
                      className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-orange-500/20 blur-3xl"
                    />
                  ) : null}

                  <h3 className="text-lg font-semibold text-white">{p.name}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-zinc-500">
                    {p.blurb}
                  </p>

                  <p className="mt-7 flex items-baseline gap-1.5">
                    <span
                      className={
                        p.highlight
                          ? "bg-gradient-to-br from-white via-orange-100 to-orange-300 bg-clip-text text-4xl font-bold tracking-tight text-transparent sm:text-5xl"
                          : "text-4xl font-bold tracking-tight text-white sm:text-5xl"
                      }
                    >
                      {p.price}
                    </span>
                    {p.period ? (
                      <span className="text-sm text-zinc-500">{p.period}</span>
                    ) : null}
                  </p>

                  <motion.ul
                    className="mt-7 space-y-3"
                    variants={
                      reduce
                        ? undefined
                        : {
                            hidden: {},
                            show: {
                              transition: { staggerChildren: 0.045 },
                            },
                          }
                    }
                  >
                    {p.features.map((f) => (
                      <motion.li
                        key={f}
                        variants={reduce ? undefined : listItem}
                        className="flex items-start gap-2.5 text-sm text-zinc-300"
                      >
                        <span
                          className={
                            p.highlight
                              ? "mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-orange-500/20 text-orange-400"
                              : "mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-white/5 text-orange-400/80"
                          }
                        >
                          <Check className="h-3 w-3" strokeWidth={2.5} />
                        </span>
                        {f}
                      </motion.li>
                    ))}
                  </motion.ul>

                  {p.tier === "enterprise" ? (
                    <a
                      href="mailto:matt@mrdula.solutions?subject=OpenGateway%20Enterprise"
                      className={`${btnClass} mt-auto`}
                    >
                      Talk to us
                    </a>
                  ) : p.tier === "railway" ? (
                    <RailwayCtas className={btnClass} />
                  ) : (
                    <OssCtas className={btnClass} />
                  )}
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        <p className="mt-10 text-center text-[11px] text-zinc-600">
          Software is free (Apache-2.0). Railway path bills only Railway usage on
          your account.
        </p>
      </div>
    </section>
  );
}
